import React, { useEffect, useState } from "react";
import { Button, Dropdown, Empty } from "antd";
import { LeftOutlined, MoreOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

/**
 * Mobile card list shared by the Speakers and Exhibitors tabs (replaces their wide tables).
 * Each caller supplies how to render a record; this component owns layout, the "⋮" menu,
 * tap-to-edit and prev/next paging.
 */
const EntityCardList = ({
  data,
  avatar,
  title,
  subtitle,
  tags,
  details,
  menuItems,
  onTap,
  emptyText,
  pageSize = 10,
}) => {
  const { i18n } = useTranslation();
  const isRtl = i18n.dir?.() === "rtl";
  const [page, setPage] = useState(1);

  const lastPage = Math.max(1, Math.ceil(data.length / pageSize));
  useEffect(() => {
    if (page > lastPage) setPage(lastPage);
  }, [page, lastPage]);

  if (data.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} style={{ padding: "16px 0" }} />;
  }

  const start = (page - 1) * pageSize;
  const pageItems = data.slice(start, start + pageSize);
  const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {pageItems.map((record) => {
          const tagNodes = tags?.(record);
          const detailNodes = details?.(record);
          return (
            <div
              key={record.id}
              onClick={() => onTap(record)}
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: 10,
                padding: 12,
                background: "#fff",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {avatar(record)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, ...ellipsis }}>{title(record)}</div>
                  {subtitle?.(record) && (
                    <div style={{ fontSize: 12, color: "#888", ...ellipsis }}>{subtitle(record)}</div>
                  )}
                </div>
                <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
                  <Dropdown menu={{ items: menuItems(record) }} trigger={["click"]} placement="bottomRight">
                    <Button type="text" size="large" icon={<MoreOutlined style={{ fontSize: 18 }} />} />
                  </Dropdown>
                </div>
              </div>

              {tagNodes && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>{tagNodes}</div>
              )}

              {detailNodes && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    marginTop: 8,
                    fontSize: 12,
                    color: "#595959",
                  }}
                >
                  {detailNodes}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data.length > pageSize && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
          <span style={{ fontSize: 12, color: "#8c8c8c" }}>
            {start + 1}–{Math.min(start + pageSize, data.length)} / {data.length}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              icon={isRtl ? <RightOutlined /> : <LeftOutlined />}
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            />
            <Button
              icon={isRtl ? <LeftOutlined /> : <RightOutlined />}
              disabled={page >= lastPage}
              onClick={() => setPage(page + 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EntityCardList;
